import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Stack,
  Chip,
  Box,
} from '@mui/material';
import { Close as CloseIcon, Map as MapIcon } from '@mui/icons-material';
import { TimelineShape } from './TimelineSchedule';
import { TimelineMap } from './TimelineMap';

interface TimelineMapDialogProps {
  timeline: TimelineShape;
  departureDate?: string;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}

export const TimelineMapDialog: React.FC<TimelineMapDialogProps> = ({
  timeline,
  departureDate,
  isOpen,
  onClose,
  onOpen,
}) => {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Get all unique days from accommodations and engagements
  const availableDays = useMemo(() => {
    const days = new Set<number>();

    // Add days from accommodations (check-in to check-out range)
    timeline.accommodations.forEach((acc) => {
      for (let day = acc.checkIn.day; day < acc.checkOut.day; day++) {
        days.add(day);
      }
    });

    // Add days from engagements
    timeline.engagements.forEach((eng) => {
      days.add(eng.start.time.day);
    });

    return Array.from(days).sort((a, b) => a - b);
  }, [timeline]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<MapIcon />}
        onClick={onOpen}
        sx={{ textTransform: 'none' }}
      >
        View Map
      </Button>

      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '85vh',
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>Itinerary Map</span>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {/* Day Selection Pills */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label="Overview"
                variant={selectedDay === null ? 'filled' : 'outlined'}
                color={selectedDay === null ? 'primary' : 'default'}
                onClick={() => setSelectedDay(null)}
                sx={{ cursor: 'pointer' }}
              />
              {availableDays.map((day) => (
                <Chip
                  key={day}
                  label={`Day ${day}`}
                  variant={selectedDay === day ? 'filled' : 'outlined'}
                  color={selectedDay === day ? 'primary' : 'default'}
                  onClick={() => setSelectedDay(day)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>

          {/* Map Container */}
          <Box sx={{ flexGrow: 1, height: '100%' }}>
            {isOpen && (
              <TimelineMap
                timeline={timeline}
                departureDate={departureDate}
                selectedDay={selectedDay}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
